import { Dialog, DropdownMenu, Popover, Tooltip } from 'radix-ui';
import { Check, ChevronRight, X } from 'lucide-react';
import {
  forwardRef,
  useMemo,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';
import { Button, IconButton, type ButtonProps } from './button';

/* ============================================================================
   Overlays — dialog, drawer, menu, popover, tooltip.

   All four were hand-rolled before: a `fixed inset-0` click-catcher, no focus
   trap, no `Escape`, no `aria-*`, and no return of focus to the trigger. Radix
   supplies the behaviour; this file supplies the one look they share.
   ========================================================================== */

const overlayClass =
  'fixed inset-0 z-50 bg-black/35 backdrop-blur-[2px] data-[state=open]:animate-fade-in';

const panelClass = [
  'fixed z-50 flex flex-col bg-surface-raised shadow-overlay',
  'focus:outline-none',
];

/**
 * Put focus back where it came from when an overlay closes.
 *
 * Radix returns focus to a `Dialog.Trigger`. These overlays are opened from
 * ordinary buttons under application state instead, so there is no Trigger to
 * return to — Radix focuses a null ref and a keyboard user who opens Share and
 * presses Escape is dropped onto `<body>`, at the very top of the document.
 *
 * Both halves are timed against Radix's focus scope:
 *
 * - Capture on `onOpenAutoFocus`, which the scope dispatches after mounting but
 *   *before* it moves focus inside, so the trigger is still the active element.
 *   Neither render nor an effect works here: the panel component's body runs on
 *   every parent render, closed included, because it is Radix that gates the
 *   portal — the first run happens long before the overlay is ever opened.
 * - Restore on `onCloseAutoFocus`. Radix composes the caller's handler ahead of
 *   its own and skips its own once the event is defaultPrevented, which is what
 *   makes taking the restore over possible.
 *
 * A trigger that has since left the document is left alone, so a dialog that
 * hands off to another screen is never yanked backwards.
 */
export function useReturnFocus(): {
  onOpenAutoFocus: (event: Event) => void;
  onCloseAutoFocus: (event: Event) => void;
} {
  const trigger = useRef<HTMLElement | null>(null);

  return useMemo(
    () => ({
      onOpenAutoFocus: () => {
        const active = document.activeElement;
        trigger.current = active instanceof HTMLElement && active !== document.body ? active : null;
      },
      onCloseAutoFocus: (event: Event) => {
        const target = trigger.current;
        trigger.current = null;
        if (!target?.isConnected) return;
        event.preventDefault();
        target.focus({ preventScroll: true });
      },
    }),
    [],
  );
}

// ── Dialog ──────────────────────────────────────────────────────────────────

export const Modal = Dialog.Root;
export const ModalTrigger = Dialog.Trigger;
export const ModalClose = Dialog.Close;

export interface ModalContentProps extends Omit<ComponentPropsWithoutRef<typeof Dialog.Content>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  /** Rendered in the footer, right-aligned. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Hide the header's close button (a required decision, for instance). */
  hideClose?: boolean;
  bodyClassName?: string;
}

const MODAL_SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-3xl',
} as const;

export const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>(function ModalContent(
  { className, bodyClassName, title, description, footer, size = 'md', hideClose, children, onOpenAutoFocus, onCloseAutoFocus, ...props },
  ref,
) {
  const returnFocus = useReturnFocus();
  return (
    <Dialog.Portal>
      <Dialog.Overlay className={overlayClass} />
      <Dialog.Content
        ref={ref}
        onOpenAutoFocus={(event) => {
          returnFocus.onOpenAutoFocus(event);
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          if (!event.defaultPrevented) returnFocus.onCloseAutoFocus(event);
        }}
        className={cn(
          panelClass,
          'left-1/2 top-1/2 max-h-[min(90dvh,52rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
          'overflow-hidden rounded-xl border border-border data-[state=open]:animate-scale-in',
          MODAL_SIZES[size],
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-3.5">
          <div className="min-w-0">
            <Dialog.Title className="font-display text-[0.9375rem] font-semibold text-fg">{title}</Dialog.Title>
            {description && (
              <Dialog.Description className="mt-1 text-xs leading-relaxed text-fg-muted">
                {description}
              </Dialog.Description>
            )}
          </div>
          {!hideClose && (
            <Dialog.Close asChild>
              <IconButton label="Close" size="sm">
                <X />
              </IconButton>
            </Dialog.Close>
          )}
        </div>
        <div className={cn('scroll-region min-h-0 flex-1 px-5 py-4', bodyClassName)}>{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle bg-surface-subtle px-5 py-3">
            {footer}
          </div>
        )}
      </Dialog.Content>
    </Dialog.Portal>
  );
});

// ── Drawer (side sheet) ─────────────────────────────────────────────────────

export const Drawer = Dialog.Root;
export const DrawerTrigger = Dialog.Trigger;
export const DrawerClose = Dialog.Close;

export interface DrawerContentProps extends Omit<ComponentPropsWithoutRef<typeof Dialog.Content>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  side?: 'right' | 'left' | 'bottom';
  width?: string;
  bodyClassName?: string;
}

export const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(function DrawerContent(
  {
    className,
    bodyClassName,
    title,
    description,
    footer,
    side = 'right',
    width = '26rem',
    children,
    onOpenAutoFocus,
    onCloseAutoFocus,
    ...props
  },
  ref,
) {
  const returnFocus = useReturnFocus();
  const sideClass =
    side === 'bottom'
      ? 'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-xl border-t data-[state=open]:animate-slide-up'
      : side === 'left'
        ? 'inset-y-0 left-0 border-r data-[state=open]:animate-slide-in-left'
        : 'inset-y-0 right-0 border-l data-[state=open]:animate-slide-in-right';
  return (
    <Dialog.Portal>
      <Dialog.Overlay className={overlayClass} />
      <Dialog.Content
        ref={ref}
        onOpenAutoFocus={(event) => {
          returnFocus.onOpenAutoFocus(event);
          onOpenAutoFocus?.(event);
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          if (!event.defaultPrevented) returnFocus.onCloseAutoFocus(event);
        }}
        style={side === 'bottom' ? undefined : { width, maxWidth: 'calc(100vw - 2rem)' }}
        className={cn(panelClass, 'border-border', sideClass, className)}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-3.5">
          <div className="min-w-0">
            <Dialog.Title className="font-display text-[0.9375rem] font-semibold text-fg">{title}</Dialog.Title>
            {description && (
              <Dialog.Description className="mt-1 text-xs leading-relaxed text-fg-muted">
                {description}
              </Dialog.Description>
            )}
          </div>
          <Dialog.Close asChild>
            <IconButton label="Close" size="sm">
              <X />
            </IconButton>
          </Dialog.Close>
        </div>
        <div className={cn('scroll-region min-h-0 flex-1 px-5 py-4', bodyClassName)}>{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-border-subtle bg-surface-subtle px-5 py-3">
            {footer}
          </div>
        )}
      </Dialog.Content>
    </Dialog.Portal>
  );
});

// ── Confirmation ────────────────────────────────────────────────────────────

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** What will happen, in the user's terms. Name the thing being changed. */
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ButtonProps['variant'];
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
}

/**
 * Confirmation for an action that cannot be undone.
 *
 * Reserved for exactly that: a reversible action should just happen. Before this
 * existed, deleting a chart took one click with no confirmation at all, while
 * renaming one asked twice.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  variant = 'danger',
  busy,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        size="sm"
        title={title}
        footer={
          <>
            <Dialog.Close asChild>
              <Button variant="secondary">{cancelLabel}</Button>
            </Dialog.Close>
            <Button variant={variant} loading={busy} onClick={() => void onConfirm()}>
              {confirmLabel}
            </Button>
          </>
        }
      >
        <div className="text-[0.8125rem] leading-relaxed text-fg-secondary">{body}</div>
      </ModalContent>
    </Modal>
  );
}

// ── Dropdown menu ───────────────────────────────────────────────────────────

export const Menu = DropdownMenu.Root;
export const MenuTrigger = DropdownMenu.Trigger;
export const MenuSub = DropdownMenu.Sub;
export const MenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenu.Separator>
>(function MenuSeparator({ className, ...props }, ref) {
  return <DropdownMenu.Separator ref={ref} className={cn('my-1 h-px bg-border-subtle', className)} {...props} />;
});

const menuPanel = [
  'z-50 min-w-[11rem] overflow-hidden rounded-lg border border-border bg-surface-raised p-1 shadow-overlay',
  'data-[state=open]:animate-scale-in',
];

export const MenuContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof DropdownMenu.Content>>(
  function MenuContent({ className, sideOffset = 6, align = 'end', ...props }, ref) {
    return (
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          ref={ref}
          sideOffset={sideOffset}
          align={align}
          collisionPadding={8}
          className={cn(menuPanel, className)}
          {...props}
        />
      </DropdownMenu.Portal>
    );
  },
);

const menuItemClass = [
  'flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-[0.8125rem] text-fg-secondary outline-none',
  'data-[highlighted]:bg-surface-hover data-[highlighted]:text-fg',
  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-fg-faint data-[highlighted]:[&_svg]:text-fg-muted',
];

export const MenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenu.Item> & { destructive?: boolean; shortcut?: string }
>(function MenuItem({ className, destructive, shortcut, children, asChild, ...props }, ref) {
  return (
    <DropdownMenu.Item
      ref={ref}
      asChild={asChild}
      className={cn(
        menuItemClass,
        destructive && 'text-danger data-[highlighted]:bg-danger-bg data-[highlighted]:text-danger [&_svg]:text-danger',
        className,
      )}
      {...props}
    >
      {/*
        With `asChild` the caller's element IS the item, and Radix's Slot accepts
        exactly one child — a shortcut span beside it would be a second one. So an
        `asChild` item composes its own content; anything else gets the shortcut.
      */}
      {asChild ? (
        children
      ) : (
        <>
          {children}
          {shortcut && <span className="ml-auto font-mono text-[0.625rem] text-fg-faint">{shortcut}</span>}
        </>
      )}
    </DropdownMenu.Item>
  );
});

export const MenuCheckboxItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenu.CheckboxItem>
>(function MenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenu.CheckboxItem ref={ref} className={cn(menuItemClass, 'pl-7', className)} {...props}>
      <DropdownMenu.ItemIndicator className="absolute left-2 flex">
        <Check className="size-3.5" strokeWidth={3} />
      </DropdownMenu.ItemIndicator>
      {children}
    </DropdownMenu.CheckboxItem>
  );
});

export const MenuRadioGroup = DropdownMenu.RadioGroup;

export const MenuRadioItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenu.RadioItem>
>(function MenuRadioItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenu.RadioItem ref={ref} className={cn(menuItemClass, 'pl-7', className)} {...props}>
      <DropdownMenu.ItemIndicator className="absolute left-2 flex">
        <Check className="size-3.5" strokeWidth={3} />
      </DropdownMenu.ItemIndicator>
      {children}
    </DropdownMenu.RadioItem>
  );
});

export const MenuLabel = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof DropdownMenu.Label>>(
  function MenuLabel({ className, ...props }, ref) {
    return (
      <DropdownMenu.Label
        ref={ref}
        className={cn('px-2 pb-1 pt-1.5 text-[0.625rem] font-semibold uppercase tracking-wider text-fg-faint', className)}
        {...props}
      />
    );
  },
);

export const MenuSubTrigger = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenu.SubTrigger>
>(function MenuSubTrigger({ className, children, ...props }, ref) {
  return (
    <DropdownMenu.SubTrigger ref={ref} className={cn(menuItemClass, className)} {...props}>
      {children}
      <ChevronRight className="ml-auto" />
    </DropdownMenu.SubTrigger>
  );
});

export const MenuSubContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenu.SubContent>
>(function MenuSubContent({ className, ...props }, ref) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.SubContent ref={ref} sideOffset={4} className={cn(menuPanel, className)} {...props} />
    </DropdownMenu.Portal>
  );
});

// ── Popover ─────────────────────────────────────────────────────────────────

export const Pop = Popover.Root;
export const PopTrigger = Popover.Trigger;
export const PopAnchor = Popover.Anchor;
export const PopClose = Popover.Close;

export const PopContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Popover.Content>>(
  function PopContent({ className, sideOffset = 6, align = 'end', ...props }, ref) {
    return (
      <Popover.Portal>
        <Popover.Content
          ref={ref}
          sideOffset={sideOffset}
          align={align}
          collisionPadding={8}
          className={cn(
            'z-50 rounded-lg border border-border bg-surface-raised p-3 shadow-overlay outline-none',
            'data-[state=open]:animate-scale-in',
            className,
          )}
          {...props}
        />
      </Popover.Portal>
    );
  },
);

// ── Tooltip ─────────────────────────────────────────────────────────────────

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <Tooltip.Provider delayDuration={350} skipDelayDuration={200}>
      {children}
    </Tooltip.Provider>
  );
}

export interface HintProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** A keyboard shortcut shown after the text. */
  shortcut?: string;
}

/**
 * A tooltip. Never the only place information lives: it explains a control that
 * already has an accessible name, and it is not reachable on touch.
 */
export function Hint({ content, children, side = 'top', shortcut }: HintProps) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-50 flex max-w-56 items-center gap-2 rounded-md bg-surface-inverse px-2 py-1 text-xs font-medium text-fg-inverse shadow-md',
            'data-[state=delayed-open]:animate-fade-in',
          )}
        >
          {content}
          {shortcut && (
            <kbd className="rounded border border-white/20 px-1 font-mono text-[0.625rem] opacity-80">{shortcut}</kbd>
          )}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
